import { buildKeyTakeaways, type KeyTakeawaysInput } from "@/lib/key-takeaways";
import { keyFindingsHeading } from "@/lib/article-variation";

type Props = KeyTakeawaysInput & {
  /** Heading text — defaults to the AI-citation friendly "Key Findings". */
  heading?: string;
  /** Article slug — varies the default heading wording per article. */
  slug?: string;
  id?: string;
  /** Use on dark hero backgrounds where foreground text would be unreadable. */
  tone?: "default" | "onDark";
};

/**
 * Near-the-top TL;DR block: an H2 followed by 3-5 concise bullets that
 * summarise the page for readers and AI answer engines.
 */
export function KeyTakeaways({
  heading,
  slug,
  id = "key-findings",
  tone = "default",
  ...input
}: Props) {
  const title = heading ?? (slug ? keyFindingsHeading(slug) : "Key Findings");
  const points = buildKeyTakeaways(input);
  if (points.length < 3) return null;

  return (
    <section
      aria-labelledby={id}
      className={`article-summary not-prose my-6 rounded-xl border border-accent/30 p-5 sm:p-6 ${
        tone === "onDark" ? "bg-white/5" : "bg-accent/5"
      }`}
    >
      <h2
        id={id}
        className="m-0 font-display text-lg font-bold uppercase tracking-wide text-accent sm:text-xl"
      >
        {title}
      </h2>
      <ul className="mt-3 space-y-2 pl-0">
        {points.map((point, i) => (
          <li key={i} className={`flex gap-2 text-sm leading-relaxed sm:text-base ${
              tone === "onDark" ? "text-white/85" : "text-foreground"
            }`}>
            <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
            <span>{point}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
